import { MEAL_KEYS, MEAL_NAME } from "components/StandingOrder/format";
import { TStatusDay, TStatusMeal } from "components/StandingOrder/types";

export const STATE_LABEL: { [state: string]: string } = {
  standing: "Standing",
  changed: "Changed",
  skipped: "Skipped",
  needs_you: "Needs you",
};

// standingOrder: 1 makes FullCalendar list the chips before the order/dish lines
// (eventOrder), so they are never pushed behind "+N more".
export type TBadgeEvent = {
  date: string;
  title: string;
  className: string;
  standingOrder?: number;
};

/** The calendar's eventOrder: standing chips first, then by start as before. */
export const STANDING_EVENT_ORDER = "standingOrder,start";

const chip = (date: string, title: string, modifier: string): TBadgeEvent => ({
  date,
  title,
  className: `event standing-badge standing-badge--${modifier}`,
  standingOrder: 1,
});

/**
 * Compact calendar chips from GET /standing-orders/status (Calendar badges v2):
 * - a closed (past-deadline) weekday gets ONLY "Closed": nothing can change
 *   there, and a past hand order must not read as "you changed it by hand";
 * - a holiday gets no state chips;
 * - a day whose standing meals are all "standing" gets ONE "Standing order" chip;
 * - a day whose standing meals are all skipped gets ONE "Skipped" chip;
 * - otherwise each meal that is not "standing" gets its own "Lunch: Changed" /
 *   "Lunch: Skipped" / "Lunch: Needs you" chip, and standing meals get none.
 * Meals in state "none" never count.
 */
export const getStandingBadges = (days: TStatusDay[]): TBadgeEvent[] => {
  const events: TBadgeEvent[] = [];
  (Array.isArray(days) ? days : []).forEach((day) => {
    if (!day) return;
    if (day.locked) {
      events.push(chip(day.date, "Closed", "closed"));
      return;
    }
    if (day.holiday) return;
    const meals = MEAL_KEYS.map((m) =>
      (day.meals || []).find((x) => x.meal === m)
    ).filter((x) => x && STATE_LABEL[x.state]) as TStatusMeal[];
    if (!meals.length) return;
    if (meals.every((x) => x.state === "standing")) {
      events.push(chip(day.date, "Standing order", "standing"));
    } else if (meals.every((x) => x.state === "skipped")) {
      events.push(chip(day.date, "Skipped", "skipped"));
    } else {
      meals
        .filter((x) => x.state !== "standing")
        .forEach((x) =>
          events.push(
            chip(
              day.date,
              `${MEAL_NAME[x.meal]}: ${STATE_LABEL[x.state]}`,
              x.state.replace("_", "-")
            )
          )
        );
    }
  });
  return events;
};



/**
 * Add the badges to the calendar's events. With no badges the original value
 * is returned untouched, so a school without standing orders gets exactly the
 * events it always had.
 */
export const withStandingBadges = (
  events: any[] | null | undefined,
  badges: TBadgeEvent[] | undefined
): any => {
  if (!badges || !badges.length) return events ? events : {};
  return [...badges, ...(events || [])];
};
